import { useEffect } from "react";

import { Outlet, useLoaderData, useLocation, useNavigation } from "react-router";

import { ChatWidgetPrivate } from "@features/ChatWidget";
import ErrorBoundary from "@features/ErrorBoundary";
import { GoalsContainer } from "@features/goals/GoalsContainer";
import { PrivateRequestBar } from "@features/privateRequest/PrivateRequestBar";
import { PrivateRequestPopUpContainer } from "@features/privateRequest/PrivateRequestPopUpContainer";
import { ToyControlProposalPopUp } from "@features/toys/ToyControlProposalPopUp";
import { ToysCompactView } from "@features/toys/ToysCompactView";
import { ToysContainer } from "@features/toys/ToysContainer";

import { Spinner } from "@components/Spinner";
import { Stack } from "@components/Stack";

import { useLayoutBreakpoint } from "@hooks/useLayoutBreakpoint";

import { EBroadcastStatus } from "@api/broadcast";

import { userAppInfoStore } from "@storage/global/useAppInfoStore";
import { useMessengerStore } from "@storage/global/useMessengerStore";
import { useToysStore } from "@storage/global/useToysStore";
import { useBlacklistQuery } from "@storage/queries/blacklist";
import { useBroadcastStatusDataQuery } from "@storage/queries/broadcast/useBroadcastStatusDataQuery";
import { useGetAllCategoriesUsersQuery } from "@storage/queries/messenger";
import { useTipMenuStatusQuery } from "@storage/queries/tipMenu";
import { TSessionData } from "@storage/session/types";

import { useAuthMessenger } from "@services/messenger-sdk/useAuthMessenger";
import { WebSocketManager } from "@services/webSocket";

import { Lovense } from "@lib/lovence";
import { cn } from "@lib/utils";

import { getDeviceType } from "@utils/deviceDetect";
import { getBrowserDetails, getBrowserInfo } from "@utils/getBrowserInfo";

import { ROUTES } from "@configs/routes";

import { SidebarLeft } from "./SidebarLeft";
import { Topbar } from "./Topbar";
import { UserBar } from "./UserBar";
import { UsersList } from "./UserList";

export const Shell = () => {
	const { DESKTOP, MAX_DESKTOP, MOBILE, TABLET, SHORT_VIEWPORT } = useLayoutBreakpoint();
	const isDesktop = DESKTOP || MAX_DESKTOP;
	const isMobile = MOBILE || TABLET;
	const { broadcastStatus } = useBroadcastStatusDataQuery();
	const { isOpenToyContainer } = useToysStore();
	const showCompactToys =
		(isMobile || SHORT_VIEWPORT) && broadcastStatus === EBroadcastStatus.PRIVATE && isOpenToyContainer;
	const { activeChat } = useMessengerStore();
	const { currentAccount } = useLoaderData<TSessionData>();
	const navigation = useNavigation();
	const isPageLoading = navigation.state === "loading";

	useBlacklistQuery();
	useAuthMessenger();
	useTipMenuStatusQuery();
	useGetAllCategoriesUsersQuery();

	const location = useLocation();
	const isHomePage = location.pathname === ROUTES.HOME;
	const { setClientInfo } = userAppInfoStore();

	useEffect(() => {
		const camExtensionScript = document.createElement("script");
		camExtensionScript.src = "https://api.lovense.com/cam-extension/static/js-sdk/broadcast.js";
		camExtensionScript.async = true;
		document.body.appendChild(camExtensionScript);
		camExtensionScript.addEventListener("load", () => {
			Lovense.init(currentAccount.bss_username);
		});
	}, []);

	useEffect(() => {
		const { browserName } = getBrowserDetails();
		// Log app and client diagnostics once per page load
		setClientInfo({
			currentBrowser: getBrowserInfo(),
			currentBrowserName: browserName,
			currentDeviceType: getDeviceType(),
		});
	}, []);

	return (
		<div
			className="md:py-8 flex min-h-screen justify-center bg-ground py-0"
			style={activeChat !== null && isMobile ? { display: "none" } : {}}
		>
			<div
				className={`tablet:max-w-tablet desktop:max-w-desktop grid w-full grid-rows-[auto_1fr] overflow-hidden
					max-desktop:w-auto`}
			>
				<div
					className={`grid grid-cols-1 gap-4 tablet:grid-cols-[64px_1fr] desktop:grid-cols-[64px_1fr_324px]
						desktop:gap-0 max-desktop:gap-4`}
				>
					<aside className="hidden h-full tablet:block">
						<SidebarLeft />
					</aside>

					<div className="flex w-full min-w-0 flex-col px-4 tablet:pl-0 desktop:pr-0 max-desktop:w-143">
						<header className="col-span-full mt-3 mb-2.5 flex items-center justify-between text-white">
							<Topbar />
						</header>
						<main
							className={cn(
								"invisible-scrollbar relative h-[calc(100vh-72px)] bg-ground",
								isHomePage
									? `flex flex-col overflow-hidden [&>*:first-child]:shrink-0 [&>*:nth-child(2)]:min-h-0
										[&>*:nth-child(2)]:flex-1`
									: "block overflow-auto",
							)}
						>
							<ErrorBoundary pathname={location.pathname}>
								<div className={cn(isHomePage ? "block" : "hidden")}>
									{showCompactToys ? <ToysCompactView extraCompact={SHORT_VIEWPORT} /> : <GoalsContainer />}
									<PrivateRequestBar />
								</div>
								<Outlet />
								{isPageLoading ? (
									<div className="absolute inset-0 z-10 flex items-center justify-center bg-ground/80">
										<Spinner className="size-8" />
									</div>
								) : null}
							</ErrorBoundary>
						</main>
					</div>

					{isDesktop ? (
						<aside className="relative hidden desktop:block">
							<Stack wide className="h-screen px-2 pt-3">
								<UserBar />
								<UsersList />
							</Stack>
							{!SHORT_VIEWPORT ? (
								<div className="absolute right-2 bottom-0 z-docked">
									<ToysContainer />
								</div>
							) : null}
						</aside>
					) : null}
				</div>
			</div>
			<PrivateRequestPopUpContainer />
			<ToyControlProposalPopUp />
			<WebSocketManager />
			<ChatWidgetPrivate />
			<style>
				{`
					#gimmick {
						display: block;
						pointer-events: none;
						position: absolute;
						top: 0;
						width: 100%;
						height: 100%;
						left: 0;
						right: 0;
					}
				`}
			</style>
		</div>
	);
};
